<template>
  <v-container v-if="loadView" :class="{'pa-2': $vuetify.breakpoint.xsOnly}" align="center">
    <v-form ref="form"
      @submit="checkForm"
      novalidate="true">
      <!-- BOOKING INFO -->
      <section-block :title="$t('sections.booking_info')">
        <template v-slot:aside>
          <h4 class="primary--text pt-2 font-weight-light">#{{ state.bookingId }}</h4>
        </template>
        <template v-slot:body>
          <v-row>
            <v-col cols="12" sm="6" class="py-0">
              <v-autocomplete :label="$t('form.mandatory.facility')"
                :items="facilities.list"
                item-text="name"
                item-value="id"
                v-model="facilityId"
                :error-messages="!facilityId && showErrors ? $t('errors.mandatory') : []">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-autocomplete :label="$t('form.status')"
                :items="bookingTypes.list"
                item-text="name"
                item-value="id"
                v-model="bookingTypeId">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="3" class="py-0">
              <date-input :label="$t('form.mandatory.checkin_date')"
                :initialDate="reservation.initialCheckInDate"
                @change="v => { checkin = formatCheckin({ checkInDate: v, checkInTime: reservation.currentCheckInTime }); reservation.currentCheckInDate = v }"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <time-input :label="$t('form.mandatory.checkin_time')"
                :initialTime="reservation.initialCheckInTime"
                @change="v => { checkin = formatCheckin({ checkInDate: reservation.currentCheckInDate, checkInTime: v }); reservation.currentCheckInTime = v }"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <date-input :label="$t('form.checkout_date')"
                :initialDate="reservation.initialCheckOutDate"
                @change="v => { checkout = formatCheckout({ checkOutDate: v, checkOutTime: reservation.currentCheckOutTime }); reservation.currentCheckOutDate = v }"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <time-input :label="$t('form.checkout_time')"
                :initialTime="reservation.initialCheckOutTime"
                @change="v => { checkout = formatCheckout({ checkOutDate: reservation.currentCheckOutDate, checkOutTime: v }); reservation.currentCheckOutTime = v }"/>
            </v-col>
          </v-row>
        </template>
      </section-block>
      <!-- BOOKING PROFILE -->
      <section-block :title="$t('sections.booking_profile')"
        :subtitle="'#'"
        :otherOptions="true">
        <template v-slot:aside v-if="!state.bookingId">
          <v-autocomplete class="mt-0 pt-0 aside-select"
            background-color="primary"
            solo dense flat dark hide-details
            :items="selectors.bookingProfileSelector"
            v-model="selectors.bookingProfileSelectorOption">
            <template v-slot:selection="{ item }">{{ $t('form.selector_values.' + item) }}</template>
            <template v-slot:item="{ item }">{{ $t('form.selector_values.' + item) }}</template>
          </v-autocomplete>
        </template>
        <template v-slot:body>
          <v-row>
            <v-col cols="6" sm="3" class="py-0">
              <v-text-field :label="$t('form.mandatory.name')"
                v-model="name"
                :error-messages="!name && showErrors ? $t('errors.mandatory') : []"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <v-text-field :label="$t('form.last_name')"
                v-model="lastName"/>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field :label="$t('form.email')"
                v-model="email"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="3" class="py-0">
              <v-text-field :label="$t('form.phone_number')"
                v-model="phoneNumber"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <date-input :clearable="true" :label="$t('form.birth_date')"
                :initialDate="dateOfBirth"
                @change="v => dateOfBirth = formatDateOfBirth(v)"/>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-autocomplete :label="$t('form.language')"
                :items="languageCodes"
                item-text="name"
                item-value="code"
                v-model="languageCode">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-radio-group :label="$t('form.gender')" v-model="gender">
                <v-row>
                  <v-col>
                    <div class="d-inline-flex flex-wrap">
                      <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                        <v-radio key="male"
                          :label="$t('form.genders.MALE')"
                          value="MALE"/>
                      </v-flex>
                      <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                        <v-radio key="female"
                          :label="$t('form.genders.FEMALE')"
                          value="FEMALE"/>
                      </v-flex>
                      <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                        <v-radio key="other"
                          :label="$t('form.genders.OTHER')"
                          value="OTHER"/>
                      </v-flex>
                    </div>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
        </template>
        <template v-slot:otherOptions>
          <v-row>
            <v-col cols="6" class="py-0">
              <v-autocomplete :label="$t('form.profile_type')"
                :items="profileTypes.list"
                item-text="name"
                item-value="id"
                v-model="typeId">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6" class="py-0">
              <v-autocomplete :label="$t('form.profile_category')"
                :items="profileCategories.list"
                item-text="name"
                item-value="id"
                v-model="categoryId">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </template>
      </section-block>
      <!-- ROOM -->
      <edit-room
        v-for="(room, index) in bookingRooms" :extendedRoom="room"
        :key="index"
        @input="r => updateRoom(r, index)"
        :filteredRoomList="filteredRoomList"
        :showErrors="showErrors">
      </edit-room>
      <v-container class="px-5">
        <v-row>
          <v-col align="right">
            <v-btn color="accent"
              :loading="loading"
              type="submit">
              {{ $t('actions.create') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
import moment from 'moment'
import { PANEL_LOCATION } from '@/config/environment'
import { mapState, mapGetters } from 'vuex'
import searchMixins from '@/mixins/Search.js'
import languageCodes from '@/config/languages'
import { selectorValues } from '@/config'
import { Guest } from '@/entities/Guest'
import { Profile } from '@/entities/Profile'

import { GuestRequests } from '@/services/guest.requests'
import { ProfileRequests } from '@/services/profile.requests'
const guestService = new GuestRequests()
const profileService = new ProfileRequests()

export default {
  name: 'main-view',
  data () {
    return {
      loadView: false,
      loading: false,
      submiting: false,
      loadingDefaultData: false,
      showErrors: false,
      moment,
      languageCodes: languageCodes,
      selectorValues: selectorValues,
      bookingRooms: [], // { account, room, guests }
      reservation: {
        currentCheckInDate: null,
        currentCheckInTime: null,
        currentCheckOutDate: null,
        currentCheckOutTime: null,
        initialCheckInDate: null,
        initialCheckInTime: null,
        initialCheckOutDate: null,
        initialCheckOutTime: null
      },
      selectors: {
        bookingProfileSelector: [selectorValues.NEW],
        bookingProfileSelectorOption: selectorValues.NEW
      },
      room: {
        roomInfoList: []
      }
    }
  },
  components: {
    SectionBlock: () => import('@/components/SectionBlock.vue'),
    ExpansionSectionBlock: () => import('@/components/ExpansionSectionBlock.vue'),
    DateInput: () => import('@/components/app-components/DateInput.vue'),
    TimeInput: () => import('@/components/app-components/TimeInput.vue'),
    EditRoom: () => import('@/components/view-components/EditRoom.vue')
  },
  mixins: [
    searchMixins
  ],
  created () {
    Promise.all([
      this.$store.dispatch('facilities/list'),
      this.$store.dispatch('bookingTypes/list'),
      this.$store.dispatch('profileTypes/list'),
      this.$store.dispatch('profileCategories/list'),
      this.$store.dispatch('guestTypes/list'),
      this.$store.dispatch('guestCategories/list'),
      this.$store.dispatch('rooms/list')
    ]).then(() => {
      this.loadExistingBookingData()
      this.loadView = true
    })
  },
  methods: {
    checkForm (e) {
      // Submit loading button
      this.loading = true
      // Prevent default event
      e.preventDefault()
      // Errors
      var errors = this.findErrors()
      // Submit
      if (!errors) {
        this.edit()
      } else {
        this.showErrors = true
        this.scrollToTop()
        this.submitMessage(this.$t('post.error_message'), 'error', 5000)
      }
    },
    findErrors () {
      if (!this.facilityId) {
        return true
      }
      if (!this.name) {
        return true
      }
      for (var roomInfo of this.room.roomInfoList) {
        for (var guestInfo of roomInfo.guests) {
          if (!guestInfo.profile.name) {
            return true
          }
        }
      }
      return false
    },
    updateRoom (room, index) {
      this.bookingRooms[index] = room
    },
    async loadExistingBookingData () {
      this.$store.dispatch('bookings/get', { id: this.state.bookingId }).then((booking) => {
        this.reservation.currentCheckInDate = moment(booking.checkin).format('YYYY-MM-DD')
        this.reservation.currentCheckOutDate = moment(booking.checkout).format('YYYY-MM-DD')
        this.reservation.currentCheckInTime = moment(booking.checkin).format('HH:mm')
        this.reservation.currentCheckOutTime = moment(booking.checkout).format('HH:mm')
        this.reservation.initialCheckInDate = moment(booking.checkin).format('YYYY-MM-DD')
        this.reservation.initialCheckOutDate = moment(booking.checkout).format('YYYY-MM-DD')
        this.reservation.initialCheckInTime = moment(booking.checkin).format('HH:mm')
        this.reservation.initialCheckOutTime = moment(booking.checkout).format('HH:mm')
        this.facilityId = booking.facilityId
        this.bookingTypeId = booking.typeId
        const bookingPromises = []
        bookingPromises.push(this.$store.dispatch('profiles/get', { id: booking.profileId, save: true }).then((profile) => {
          this.name = profile.name
          this.lastName = profile.lastName
          this.email = profile.email
          this.phoneNumber = profile.phoneNumber
          this.dateOfBirth = profile.dateOfBirth ? moment(profile.dateOfBirth).format('YYYY-MM-DD') : null
          this.typeId = profile.typeId
          this.categoryId = profile.categoryId
          this.languageCode = profile.languageCode
          this.gender = profile.gender
        }))
        bookingPromises.push(this.$store.dispatch('bookings/getRooms', { bookingId: this.state.bookingId }).then((bookingRooms) => {
          this.bookingRooms = bookingRooms
        }))
      })
    },
    async edit () {
      const promises = []
      // Profile
      promises.push(this.$store.dispatch('profiles/save', {}))
      // Booking
      promises.push(this.$store.dispatch('bookings/save', {}))
      // Guests
      this.bookingRooms.forEach(extendedRoom => {
        extendedRoom.guests.forEach(guest => {
          guest.roomId = extendedRoom.roomId
          promises.push(guestService.save(new Guest(guest).serialize({})))
          if (guest.profile.id !== this.profiles.read.id)
            promises.push(profileService.save(new Profile(guest.profile).serialize({})))
        })
      })
      Promise.all(promises).then(() => {
        this.closeWindow()
      })
    },
    submitMessage (text, color, timeout) {
      this.$store.commit('state/setMessage', { text: this.$t('post.success_message'), color: 'success', timeout: 5000 })
      this.$store.commit('state/setMessage', { text: text, color: color, timeout: timeout })
    },
    closeWindow () {
      if (window.opener) {
        window.opener.postMessage('refreshData', PANEL_LOCATION)
        window.close()
      }
    },
    formatDateOfBirth (value) {
      return value ? moment(value).add(12,'h').format('YYYY-MM-DDTHH:mm:ss.SSSZZ') : null
    },
    formatCheckin ({ checkInDate, checkInTime }) {
      if (checkInDate && checkInTime) {
        return moment(checkInDate + 'T' + checkInTime).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
      }
    },
    formatCheckout ({ checkOutDate, checkOutTime }) {
      if (checkOutDate && checkOutTime) {
        return moment(checkOutDate + 'T' + checkOutTime).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
      }
    },
    scrollToTop () {
      this.$refs.form.$el.scrollIntoView({ behavior: 'smooth' })
    }
  },
  computed: {
    ...mapState(['state', 'facilities', 'bookingTypes', 'profiles', 'bookings', 'profileTypes', 'profileCategories', 'accounts', 'transactions', 'rooms', 'guestTypes', 'guestCategories', 'guests', 'uuids']),
    ...mapGetters('rooms', ['getRoomName', 'getFirstFilteredRoomId']),
    facilityId: {
      get () {
        return this.bookings.edited ? this.bookings.edited.facilityId : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'facilityId', value })
      }
    },
    bookingTypeId: {
      get () {
        return this.bookings.edited ? this.bookings.edited.typeId : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'typeId', value })
      }
    },
    checkin: {
      get () {
        return this.bookings.edited ? this.bookings.edited.checkin : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'checkin', value })
      }
    },
    checkout: {
      get () {
        return this.bookings.edited ? this.bookings.edited.checkout : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'checkout', value })
      }
    },
    name: {
      get () {
        return this.profiles.edited ? this.profiles.edited.name : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'name', value })
      }
    },
    lastName: {
      get () {
        return this.profiles.edited ? this.profiles.edited.lastName : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'lastName', value })
      }
    },
    email: {
      get () {
        return this.profiles.edited ? this.profiles.edited.email : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'email', value })
      }
    },
    phoneNumber: {
      get () {
        return this.profiles.edited ? this.profiles.edited.phoneNumber : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'phoneNumber', value })
      }
    },
    dateOfBirth: {
      get () {
        return this.profiles.edited ? this.profiles.edited.dateOfBirth : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'dateOfBirth', value })
      }
    },
    gender: {
      get () {
        return this.profiles.edited ? this.profiles.edited.gender : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'gender', value })
      }
    },
    languageCode: {
      get () {
        return this.profiles.edited ? this.profiles.edited.languageCode : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'languageCode', value })
      }
    },
    typeId: {
      get () {
        return this.profiles.edited ? this.profiles.edited.typeId : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'typeId', value })
      }
    },
    categoryId: {
      get () {
        return this.profiles.edited ? this.profiles.edited.categoryId : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'categoryId', value })
      }
    },
    modifiedRoomList: {
      get () {
        return this.rooms.modifiedRoomList || null
      },
      set (value) {
        this.$store.commit('rooms/setModifiedRoomList', value)
      }
    },
    filteredRoomList: {
      get () {
        return this.rooms.filteredRoomList || null
      },
      set (value) {
        this.$store.commit('rooms/setFilteredRoomList', value)
      }
    }
  },
  watch: {
    facilityId: {
      handler (newValue) {
        this.$store.commit('rooms/filteredRoomList', newValue)
      }
    }
  }
}
</script>

<style lang="scss">
.aside-select {
  border-radius: 10px;
  label.v-label {
    color: white !important;
  }
}
.rounded-btn {
  border-radius: 28px !important;
}
</style>
